import { clsx } from "clsx";

import RenderIf from "common/components/RenderIf";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { sequenceDelayOptions } from "../../../../constants";
import DelaySelect from "./DelaySelect";

export default function ActionDelay({
  delay,
  onDelayChange,
  isDisabled,
  labels: { action, after },
  errorMessage = null,
}: {
  // milliseconds
  delay: number;
  onDelayChange: (newDelay: number) => void;
  isDisabled: boolean;
  labels: { action: string; after: string };
  errorMessage?: string;
}) {
  const isDesktop = useTwBreakpoint("lg");

  let hours = delay / 3600000;
  const days = Math.floor(hours / 24);
  hours -= days * 24;

  const { dayOptions, hourOptions } = sequenceDelayOptions(isDesktop);
  if (days === dayOptions[dayOptions.length - 1].value) {
    hourOptions.pop();
  }
  function onChange(newValues: { days: number; hours: number }) {
    onDelayChange((newValues.hours + newValues.days * 24) * 3600000);
  }

  const daysDelayComponent = (
    <DelaySelect
      delay={days}
      onChange={(value) => onChange({ hours, days: value })}
      isDisabled={isDisabled}
      options={dayOptions}
      isError={!!errorMessage}
    />
  );
  const hoursDelayComponent = (
    <DelaySelect
      delay={hours}
      onChange={(value) => onChange({ days, hours: value })}
      isDisabled={isDisabled}
      options={hourOptions}
      isError={!!errorMessage}
    />
  );

  if (isDesktop) {
    const boxClassname = clsx(
      "flex flex-1 flex-wrap items-center justify-center rounded-3xl border px-3 py-1 text-button-xs text-black-400",
      errorMessage ? "border-red-300" : "border-black-200",
    );

    const daysLabel = `day${days === 1 ? "" : "s"}`;
    const hoursLabel = `hour${hours === 1 ? "" : "s"}`;

    return (
      <div className="flex gap-x-3">
        <div className={clsx(boxClassname, "gap-x-2")}>
          {action}
          {daysDelayComponent}
          {daysLabel}, and
          {hoursDelayComponent}
          {hoursLabel} after {after}
        </div>
        <div className={boxClassname}>
          {errorMessage ? (
            <span className="text-red-500">{errorMessage}</span>
          ) : (
            <>
              {action}&nbsp;
              {days + hours === 0 ? (
                <span className="text-black-950">immediately</span>
              ) : (
                <>
                  <RenderIf condition={days !== 0}>
                    <span className="text-black-950">
                      {days} {daysLabel}
                    </span>
                  </RenderIf>
                  <RenderIf condition={!!days && !!hours}>,&nbsp;</RenderIf>
                  <RenderIf condition={hours !== 0}>
                    <span className="text-black-950">
                      {hours} {hoursLabel}
                    </span>
                  </RenderIf>
                </>
              )}
              &nbsp;after&nbsp;<span className="text-black-950">{after}</span>
            </>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      <span
        className={clsx(
          "mb-2 text-center text-button-xs",
          errorMessage ? "text-red-500" : "text-black-400",
        )}
      >
        {errorMessage || (
          <>
            Delay {action} after <span className="text-black-950">{after}</span>
          </>
        )}
      </span>
      <div className="flex gap-x-2">
        {daysDelayComponent}
        {hoursDelayComponent}
      </div>
    </div>
  );
}
